import { useContext, useEffect, useMemo, useState, type FC } from 'react';
import InlineOptions from '../../inline-options';
import Chart from '../../chart';
import { VolSmileContext } from '../../../../providers/widgets';
import { SnapshotTypeEnum } from '../../../../model/snapshot_type';
import { lineChartColors } from '../../../../theme/colors';
import Utils from '../../../../utils/immersive_utils';
import { VOL_SMILE_TENORS_KEYS } from '../../../../providers/widgets/volatility-smile/vol-smile.constants';
import { VolatilitySmileContentProps } from './types';
import './styles.scss';
import classNames from 'classnames';
import usePersistentState from '../../../../providers/use-persistent-state';
import TimestampInfo from '../../timestamp-info';

type Chip = {
  key: string;
  color: string;
};

const VolatilitySmileContent: FC<VolatilitySmileContentProps> = ({
  providerKey,
  isSnapshotData = false,
  snapshotData = [],
  dataType = SnapshotTypeEnum.EE,
  showHeader = true,
  classname,
}) => {
  const { currentTimeStamp, type, setType, tenors, tenorKeys } = useContext(VolSmileContext);
  const data = isSnapshotData ? snapshotData : tenors;

  useEffect(() => {
    if (!isSnapshotData) return;

    setType(dataType);
  }, [dataType]);

  const chipsKeys = useMemo(() => {
    let keys: string[] = [];

    if (isSnapshotData && snapshotData) {
      try {
        keys = Object.keys(snapshotData as Record<string, any>);
      } catch {
        keys = [];
      }
    } else {
      keys = tenorKeys || [];
    }

    if (type === SnapshotTypeEnum.CM) return Utils.sortTenors(keys);
    return keys;
  }, [isSnapshotData, snapshotData, tenorKeys, type]);

  const [chips, setChips] = useState<Chip[]>([]);
  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'VOL-SMILES', dataType],
    'legendSelected',
    {},
  );

  useEffect(() => {
    if (!chipsKeys || chipsKeys.length === 0) {
      setChips([]);
      return;
    }
    setChips((prev) => {
      const prevKeys = prev.map((c) => c.key);
      const sameLength = prevKeys.length === chipsKeys.length;
      const sameOrder = sameLength && prevKeys.every((k, i) => k === chipsKeys[i]);
      if (sameOrder) return prev;
      return chipsKeys.map((k, idx) => ({
        key: k,
        color: lineChartColors[idx % lineChartColors.length],
      }));
    });
  }, [chipsKeys]);

  useEffect(() => {
    if (!chips.length) {
      return;
    }

    setLegendSelected((prev) => {
      const next: Record<string, boolean> = {};
      chips.forEach((c) => {
        next[c.key] = prev.hasOwnProperty(c.key) ? prev[c.key] : true;
      });
      return next;
    });
  }, [chips]);

  const xAxis = useMemo(() => ({ type: 'category' as const, data: VOL_SMILE_TENORS_KEYS }), []);

  const series = useMemo(() => {
    if (!data || chips.length === 0) return [] as any[];
    return chips.map((c) => {
      const values = VOL_SMILE_TENORS_KEYS.map((k) => {
        const v = data?.[c.key]?.[k];
        return v == null ? null : v;
      });
      return {
        id: c.key,
        type: 'line' as const,
        name: c.key,
        color: c.color,
        data: values,
        symbolSize: 6,
        symbol: 'circle',
        lineStyle: { type: 'solid' },
        animation: false,
        animationDuration: 0,
      };
    });
  }, [data, chips]);

  const yAxis = useMemo(
    () => ({
      type: 'value' as const,
      axisLabel: {
        formatter: (value: any) => `${(Number(value) * 100).toFixed(0)}%`,
      },
    }),
    [],
  );

  return (
    <div className={classNames('volatility-smile-content', classname)}>
      {showHeader && (
        <div className="volatility-smile-header">
          <InlineOptions
            options={[
              { value: SnapshotTypeEnum.EE, label: 'Expiry' },
              { value: SnapshotTypeEnum.CM, label: 'Tenor' },
            ]}
            selectedOption={type}
            onOptionChange={(val) => setType(val as SnapshotTypeEnum)}
          />

          <TimestampInfo timestamp={new Date(currentTimeStamp).getTime()} keepLocalTime />
        </div>
      )}
      <Chart
        series={series}
        xAxis={xAxis}
        yAxis={yAxis}
        merge={false}
        grid={{ bottom: type === SnapshotTypeEnum.EE ? '70px' : '48px' }}
        legend={{
          bottom: 8,
          selected: legendSelected,
        }}
        onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
        tooltip={{
          valueFormatter: (value: any) => {
            const num = typeof value === 'number' ? value : Number(value);
            if (value == null || Number.isNaN(num)) return '-';
            return `${(num * 100).toFixed(2)}%`;
          },
        }}
        className="volatility-smile-chart"
        isLoading={!data}
      />
    </div>
  );
};

export default VolatilitySmileContent;
